'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import { createClient } from '@/lib/supabase/client';
import { jsnService } from '@/lib/supabase/jsnService';
import type { UserProfile, MemberProfile } from '@/lib/supabase/types';
import { Clock, CheckCircle, LogOut, Settings, Compass } from 'lucide-react';


export default function AuthScreen() {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [loggedInUser, setLoggedInUser] = useState<UserProfile | null>(null);
  const [memberProfile, setMemberProfile] = useState<MemberProfile | null>(null);
  const searchParams = useSearchParams();
  const verifyError = searchParams.get('verify_error');

  useEffect(() => {
    const load = async () => {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const [up, mp] = await Promise.all([
          jsnService.getUserProfileById(user.id),
          jsnService.getCurrentMemberProfile(),
        ]);
        setLoggedInUser(up);
        setMemberProfile(mp);
      }
    };
    load();
  }, []);

  const handleLogout = async () => {
    const supabase = createClient();
    await supabase.auth.signOut();
    setLoggedInUser(null);
    setMemberProfile(null);
  };

  const photoStatus = memberProfile?.photoStatus;

  return (
    <div className="min-h-screen bg-background flex">
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 relative flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute top-1/3 left-1/4 w-80 h-80 blob-primary opacity-50" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 blob-green opacity-30" />

        <div className="relative z-10">
          <Link href="/" className="flex items-center gap-2.5 no-underline">
            <AppLogo size={48} />
            <div>
              <div className="text-xs text-muted-foreground">Jamuhuri Singles Network</div>
            </div>
          </Link>
        </div>

        <div className="relative z-10 space-y-6">
          <div className="space-y-3">
            <p className="text-sm font-semibold uppercase tracking-widest" style={{color: '#008000'}}>Values-Based Kenyan Matchmaking</p>
            <h1 className="text-4xl font-black text-foreground leading-tight">
              Your Kenyan<br />
              <span className="text-gradient-red">Love Story</span><br />
              Starts Here
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed max-w-sm">
              Connect with Kenyan singles across the USA. Browse member profiles from all 47 Kenyan counties, build genuine connections, and find your person.
            </p>
          </div>

          <div className="space-y-3">
            {[
              { initials: 'WK', color: '#750000', name: 'Wanjiru K., 31', location: 'Maryland · Kiambu', text: '"JSN helped me find someone who truly understands our culture."' },
              { initials: 'OM', color: '#38761d', name: 'Otieno M., 34', location: 'Texas · Kisumu', text: '"I found my partner here. We both love nyama choma and family."' },
            ].map((card) => (
              <div key={`testimonial-${card.initials}`} className="glass-card rounded-xl p-4 flex gap-3 items-start">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0" style={{ background: card.color }}>
                  {card.initials}
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">{card.name}</div>
                  <div className="text-xs text-muted-foreground mb-1">{card.location}</div>
                  <p className="text-xs text-muted-foreground italic">{card.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-muted-foreground">
          Free to join · Premium to message · Cancel anytime
        </div>
      </div>

      <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 lg:px-12 overflow-y-auto">
        <div className="lg:hidden mb-8 flex items-start gap-2.5">
          <AppLogo size={56} />
          <div>
            <div className="text-xs text-muted-foreground leading-tight">Jamuhuri Singles Network</div>
          </div>
        </div>

        <div className="w-full max-w-md">
          {loggedInUser ? (
            <div className="fade-in space-y-5">
              <div className="bg-card border border-border rounded-2xl p-8 text-center space-y-5">
                <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto"
                  style={{ background: loggedInUser.avatarColor || '#750000' }}>
                  {loggedInUser.avatarInitials}
                </div>
                <div className="space-y-1">
                  <h2 className="text-xl font-bold text-foreground">Welcome back, {loggedInUser.displayName}!</h2>
                  <p className="text-sm text-muted-foreground">{loggedInUser.email}</p>
                </div>

                {photoStatus === 'pending' && (
                  <div className="bg-[#FFBF00]/10 border border-[#FFBF00]/20 rounded-xl p-4 text-left space-y-1">
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="flex-shrink-0" style={{ color: '#FFBF00' }} />
                      <p className="text-xs font-semibold" style={{ color: '#FFBF00' }}>Photo Pending Review</p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Your profile photo is being reviewed. You&apos;ll be able to browse Discover once approved.
                    </p>
                  </div>
                )}

                {photoStatus === 'approved' && (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-left">
                    <div className="flex items-center gap-2">
                      <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                      <p className="text-xs font-semibold text-green-600 dark:text-green-400">Profile Approved — You&apos;re live on JSN!</p>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-3">
                  <Link href="/home" className="btn-primary w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2">
                    <Compass size={16} />Go to Home
                  </Link>
                  <Link href="/account-settings" className="btn-ghost w-full py-3 rounded-xl text-sm flex items-center justify-center gap-2">
                    <Settings size={16} />Account Settings
                  </Link>
                  <button onClick={handleLogout} className="btn-ghost w-full py-3 rounded-xl text-sm flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground">
                    <LogOut size={16} />Sign Out
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex bg-muted/30 rounded-xl p-1 mb-8 border border-border">
                <button
                  onClick={() => setActiveTab('login')}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${activeTab === 'login' ? 'tab-active' : 'tab-inactive'}`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => setActiveTab('register')}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${activeTab === 'register' ? 'tab-active' : 'tab-inactive'}`}
                >
                  Create Account
                </button>
              </div>

              {activeTab === 'login' ? (
                <LoginForm
                  onSwitchToRegister={() => setActiveTab('register')}
                  verifyError={verifyError}
                />
              ) : (
                <RegisterForm
                  onSwitchToLogin={() => setActiveTab('login')}
                  onRegistrationComplete={() => setActiveTab('login')}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}